import React, { useState, useCallback } from 'react';
import {
  getFavouriteStations,
  setFavouriteStations,
  getTimeSavedThreshold,
  setTimeSavedThreshold,
} from './services/storage';
import TrainDashboard from './components/TrainDashboard';
import StationSetupModal from './components/StationSetupModal';
import ErrorBoundary from './components/ErrorBoundary';
import { CssBaseline, ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material';
import { Routes, Route, useParams } from 'react-router';
import { Station, StationInfo } from './types';

const darkTheme = responsiveFontSizes(createTheme({
  palette: {
    mode: 'dark',
  },
}));

export function MainPage({
  favouriteStations,
  onFavouritesChange,
  timeSavedThreshold,
  onTimeSavedThresholdChange,
}: {
  favouriteStations: Station[];
  onFavouritesChange: (stations: Station[]) => void;
  timeSavedThreshold: number;
  onTimeSavedThresholdChange: (threshold: number) => void;
}) {
  const { crsCode } = useParams<{ crsCode: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stationSuggestions, setStationSuggestions] = useState<StationInfo[]>([]);

  const handleFavouritesChangeInModal = (stations: Station[]) => {
    onFavouritesChange(stations);
  };

  const handleStationSuggestions = useCallback((suggestions: StationInfo[]) => {
    setStationSuggestions(suggestions);
  }, []);

  return (
    <>
      <TrainDashboard
        crsCode={crsCode}
        onChangeFavourites={() => setIsModalOpen(true)}
        favouriteStations={favouriteStations}
        onStationSuggestions={handleStationSuggestions}
        timeSavedThreshold={timeSavedThreshold}
      />
      <StationSetupModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onFavouritesChange={handleFavouritesChangeInModal}
        stationSuggestions={stationSuggestions}
        timeSavedThreshold={timeSavedThreshold}
        onTimeSavedThresholdChange={onTimeSavedThresholdChange}
      />
    </>
  );
}

function App() {
  const [favouriteStations, setFavouriteStationsState] = useState<Station[]>(() =>
    getFavouriteStations()
  );
  const [timeSavedThreshold, setTimeSavedThresholdState] = useState<number>(() =>
    getTimeSavedThreshold()
  );

  const handleFavouritesChange = (stations: Station[]) => {
    setFavouriteStations(stations);
    setFavouriteStationsState(stations);
  };

  const handleTimeSavedThresholdChange = (threshold: number) => {
    setTimeSavedThreshold(threshold);
    setTimeSavedThresholdState(threshold);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <ErrorBoundary>
        <Routes>
          <Route
            path="/"
            element={
              <MainPage
                favouriteStations={favouriteStations}
                onFavouritesChange={handleFavouritesChange}
                timeSavedThreshold={timeSavedThreshold}
                onTimeSavedThresholdChange={handleTimeSavedThresholdChange}
              />
            }
          />
          <Route
            path="/from/:crsCode"
            element={
              <MainPage
                favouriteStations={favouriteStations}
                onFavouritesChange={handleFavouritesChange}
                timeSavedThreshold={timeSavedThreshold}
                onTimeSavedThresholdChange={handleTimeSavedThresholdChange}
              />
            }
          />
        </Routes>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;